import React, { useState } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { FiUpload, FiDownload, FiCheckCircle, FiAlertCircle, FiX } from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';

interface ImportPreview {
  valid: number;
  invalid: number;
  total: number;
  errors: { row: number; field: string; message: string }[];
}

const CompanyImportPage: React.FC = () => {
  const { token } = useAdminAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setImportSuccess(false);
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!file || !token) return;

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        '/api/admin/companies/import/excel?dry_run=true',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type, let browser set it with boundary
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        setPreview({
          valid: data.valid,
          invalid: data.invalid,
          total: data.total,
          errors: data.errors || [],
        });
      }
    } catch (err: any) {
      console.error('Validation error:', err);
      setError(err.response?.data?.detail || 'เกิดข้อผิดพลาดในการตรวจสอบไฟล์');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file || !preview || !token) return;

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        '/api/admin/companies/import/excel?dry_run=false',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type, let browser set it with boundary
          },
        }
      );

      if (response.status === 200) {
        setImportSuccess(true);
      }
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || 'เกิดข้อผิดพลาดในการ import ข้อมูล');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/api/admin/companies/export/template', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // สร้าง download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const filename = `company_import_template_${new Date().toISOString().split('T')[0]}.xlsx`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template download error:', err);
      setError('ไม่สามารถดาวน์โหลด template ได้');
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setImportSuccess(false);
    setError(null);
  };

  return (
    <AdminLayout title="นำเข้าข้อมูลบริษัท">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            นำเข้าข้อมูลบริษัท
          </h1>
          <p className="text-gray-600">
            นำเข้าข้อมูลบริษัทจากไฟล์ Excel หรือ CSV แบบครั้งละหลายรายการ
          </p>
        </div>

        {/* Download Template */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <FiDownload className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">
                  ดาวน์โหลด Template
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  ใช้ template นี้เพื่อจัดเตรียมข้อมูลก่อน import
                </p>
              </div>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <FiDownload className="w-4 h-4" />
              <span>ดาวน์โหลด</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900">เกิดข้อผิดพลาด</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto p-1 text-red-400 hover:text-red-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Upload Section */}
        {!importSuccess && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              เลือกไฟล์สำหรับ Import
            </h2>

            {/* File Input */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FiUpload className="w-12 h-12 text-gray-400 mb-3" />
                <span className="text-sm font-medium text-gray-700">
                  คลิกเพื่อเลือกไฟล์
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  รองรับ .xlsx, .xls (ขนาดไม่เกิน 5MB)
                </span>
              </label>
            </div>

            {/* Selected File */}
            {file && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Validate Button */}
            {file && !preview && (
              <div className="mt-6">
                <button
                  onClick={handleValidate}
                  disabled={importing}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {importing ? 'กำลังตรวจสอบไฟล์...' : 'ตรวจสอบไฟล์'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Preview Results */}
        {preview && !importSuccess && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              ผลการตรวจสอบ
            </h2>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-600">ทั้งหมด</p>
                <p className="text-2xl font-bold text-gray-900">{preview.total}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-sm text-green-600">ถูกต้อง</p>
                <p className="text-2xl font-bold text-green-600">{preview.valid}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-sm text-red-600">ผิดพลาด</p>
                <p className="text-2xl font-bold text-red-600">{preview.invalid}</p>
              </div>
            </div>

            {/* Errors */}
            {preview.errors.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-red-600 mb-3 flex items-center space-x-2">
                  <FiAlertCircle className="w-5 h-5" />
                  <span>ข้อผิดพลาดที่พบ</span>
                </h3>
                <div className="space-y-2">
                  {preview.errors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-900">
                        <span className="font-semibold">แถวที่ {error.row}:</span>{' '}
                        <span className="font-medium">{error.field}</span> - {error.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={resetForm}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleImport}
                disabled={importing || preview.invalid > 0}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {importing ? 'กำลัง Import...' : `Import ${preview.valid} รายการ`}
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {importSuccess && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <FiCheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Import สำเร็จ!
            </h2>
            <p className="text-gray-600 mb-6">
              นำเข้าข้อมูล {preview?.valid} รายการเรียบร้อยแล้ว
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={resetForm}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Import อีกครั้ง
              </button>
              <a
                href="/admin/companies"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                ดูรายการบริษัท
              </a>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            คำแนะนำในการ Import
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">1.</span>
              <span>ดาวน์โหลด template Excel และกรอกข้อมูลตามรูปแบบที่กำหนด</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">2.</span>
              <span>ตรวจสอบความถูกต้องของข้อมูลก่อน import</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">3.</span>
              <span>ระบบจะแจ้งเตือนหากพบข้อมูลที่ไม่ถูกต้อง</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">4.</span>
              <span>สามารถ import ได้เฉพาะข้อมูลที่ผ่านการตรวจสอบเท่านั้น</span>
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CompanyImportPage;
